import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

const ORIGINS = ["Passaggio spontaneo", "Cliente esistente", "Telefonata", "Segnalazione", "Social", "Google", "Campagna", "Agente", "Altro"];

export default function CustomerEditDialog({ open, onOpenChange, customer, onSaved }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [f, setF] = useState(null);
  const [offices, setOffices] = useState([]);
  const [operators, setOperators] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open || !customer) return;
    setError("");
    setF({
      type: customer.type, first_name: customer.first_name || "", last_name: customer.last_name || "",
      company_name: customer.company_name || "", contact_person: customer.contact_person || "",
      fiscal_code: customer.fiscal_code || "", vat_number: customer.vat_number || "",
      phone_primary: customer.phone_primary || "", phone_secondary: customer.phone_secondary || "",
      email: customer.email || "", address: customer.address || "", civico: customer.civico || "", cap: customer.cap || "",
      comune: customer.comune || "", provincia: customer.provincia || "",
      office_id: customer.office_id || "", assigned_operator_id: customer.assigned_operator_id || "",
      lead_origin: customer.lead_origin || "", acquisition_date: customer.acquisition_date || "",
      notes: customer.notes || "",
      privacy_notice_provided: !!customer.privacy_notice_provided,
      privacy_notice_date: customer.privacy_notice_date || "",
      privacy_notice_version: customer.privacy_notice_version || "",
      privacy_notice_method: customer.privacy_notice_method || "",
      marketing_consent: customer.marketing_consent || "Non espresso",
      marketing_consent_date: customer.marketing_consent_date || "",
      marketing_consent_source: customer.marketing_consent_source || "",
    });
    api.get("/offices").then(({ data }) => setOffices(data)).catch(() => {});
    if (isAdmin) api.get("/users").then(({ data }) => setOperators(data.filter((u) => u.active))).catch(() => {});
  }, [open, customer, isAdmin]);

  if (!f) return null;
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const isPrivato = f.type === "Privato";

  const save = async () => {
    setError(""); setSaving(true);
    try {
      const payload = { ...f, office_id: f.office_id || null, assigned_operator_id: f.assigned_operator_id || null, acquisition_date: f.acquisition_date || null, privacy_notice_date: f.privacy_notice_date || null, marketing_consent_date: f.marketing_consent_date || null };
      const { data } = await api.put(`/customers/${customer.id}`, payload);
      toast.success("Cliente aggiornato");
      onSaved?.(data);
      onOpenChange(false);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto bg-white sm:max-w-2xl" data-testid="edit-customer-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Modifica cliente</DialogTitle>
          <DialogDescription>Aggiorna l'anagrafica. Una sola scheda per cliente.</DialogDescription>
        </DialogHeader>
        {error && <div className="rounded-lg border border-danger/25 bg-danger/10 px-3 py-2 text-sm text-danger" data-testid="edit-customer-error">{error}</div>}

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Tipologia</Label>
            <Select value={f.type} onValueChange={(v) => set("type", v)}>
              <SelectTrigger data-testid="ec-type"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="Privato">Privato</SelectItem>
                <SelectItem value="Azienda">Azienda</SelectItem>
                <SelectItem value="Condominio">Condominio</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {isPrivato ? (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Nome *</Label><Input value={f.first_name} onChange={(e) => set("first_name", e.target.value)} data-testid="ec-first-name" /></div>
              <div className="space-y-1.5"><Label>Cognome *</Label><Input value={f.last_name} onChange={(e) => set("last_name", e.target.value)} data-testid="ec-last-name" /></div>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>{f.type === "Azienda" ? "Ragione sociale *" : "Denominazione *"}</Label><Input value={f.company_name} onChange={(e) => set("company_name", e.target.value)} data-testid="ec-company-name" /></div>
              <div className="space-y-1.5"><Label>{f.type === "Azienda" ? "Referente" : "Amministratore/Referente"}</Label><Input value={f.contact_person} onChange={(e) => set("contact_person", e.target.value)} data-testid="ec-contact-person" /></div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Codice fiscale</Label><Input value={f.fiscal_code} onChange={(e) => set("fiscal_code", e.target.value)} data-testid="ec-fiscal-code" /></div>
            <div className="space-y-1.5"><Label>Partita IVA</Label><Input value={f.vat_number} onChange={(e) => set("vat_number", e.target.value)} data-testid="ec-vat" /></div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Telefono principale</Label><Input value={f.phone_primary} onChange={(e) => set("phone_primary", e.target.value)} data-testid="ec-phone" /></div>
            <div className="space-y-1.5"><Label>Telefono secondario</Label><Input value={f.phone_secondary} onChange={(e) => set("phone_secondary", e.target.value)} /></div>
          </div>
          <div className="space-y-1.5"><Label>Email</Label><Input type="email" value={f.email} onChange={(e) => set("email", e.target.value)} data-testid="ec-email" /></div>

          <div className="grid grid-cols-8 gap-3">
            <div className="col-span-4 space-y-1.5"><Label>Indirizzo (via/piazza)</Label><Input value={f.address} onChange={(e) => set("address", e.target.value)} data-testid="ec-address" /></div>
            <div className="col-span-1 space-y-1.5"><Label>Civico</Label><Input value={f.civico} onChange={(e) => set("civico", e.target.value)} data-testid="ec-civico" /></div>
            <div className="col-span-2 space-y-1.5"><Label>CAP</Label><Input value={f.cap} onChange={(e) => set("cap", e.target.value)} data-testid="ec-cap" /></div>
            <div className="col-span-1 space-y-1.5"><Label>Prov.</Label><Input value={f.provincia} maxLength={4} onChange={(e) => set("provincia", e.target.value)} data-testid="ec-provincia" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Comune</Label><Input value={f.comune} onChange={(e) => set("comune", e.target.value)} data-testid="ec-comune" /></div>
            <div className="space-y-1.5"><Label>Data acquisizione</Label><Input type="date" value={f.acquisition_date || ""} onChange={(e) => set("acquisition_date", e.target.value)} /></div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Sede di riferimento</Label>
              <Select value={f.office_id || "none"} onValueChange={(v) => set("office_id", v === "none" ? "" : v)}>
                <SelectTrigger data-testid="ec-office"><SelectValue placeholder="Nessuna" /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="none">Nessuna</SelectItem>
                  {offices.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Operatore assegnato {!isAdmin && <span className="text-slate-400">(solo Direzione)</span>}</Label>
              <Select value={f.assigned_operator_id || "none"} onValueChange={(v) => set("assigned_operator_id", v === "none" ? "" : v)} disabled={!isAdmin}>
                <SelectTrigger data-testid="ec-operator"><SelectValue placeholder="Nessuno" /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="none">Nessuno</SelectItem>
                  {operators.map((o) => <SelectItem key={o.id} value={o.id}>{o.full_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Origine cliente</Label>
            <Select value={f.lead_origin || "none"} onValueChange={(v) => set("lead_origin", v === "none" ? "" : v)}>
              <SelectTrigger data-testid="ec-origin"><SelectValue placeholder="Seleziona" /></SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="none">Non specificata</SelectItem>
                {ORIGINS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-3 rounded-lg border border-border p-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Informativa privacy</Label>
              <p className="mb-2 text-xs text-slate-400">La consegna dell'informativa non costituisce consenso al trattamento.</p>
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-700">Informativa fornita</span>
                <Switch checked={!!f.privacy_notice_provided} onCheckedChange={(v) => set("privacy_notice_provided", v)} data-testid="ec-notice-provided" />
              </div>
              <div className="mt-2 grid grid-cols-3 gap-2">
                <div className="space-y-1"><Label className="text-xs">Data</Label><Input type="date" value={f.privacy_notice_date || ""} onChange={(e) => set("privacy_notice_date", e.target.value)} data-testid="ec-notice-date" /></div>
                <div className="space-y-1"><Label className="text-xs">Versione</Label><Input value={f.privacy_notice_version} onChange={(e) => set("privacy_notice_version", e.target.value)} placeholder="es. v1.0" /></div>
                <div className="space-y-1"><Label className="text-xs">Metodo</Label>
                  <Select value={f.privacy_notice_method || "none"} onValueChange={(v) => set("privacy_notice_method", v === "none" ? "" : v)}>
                    <SelectTrigger data-testid="ec-notice-method"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent className="bg-white"><SelectItem value="none">—</SelectItem><SelectItem value="Cartaceo">Cartaceo</SelectItem><SelectItem value="Email">Email</SelectItem><SelectItem value="Digitale">Digitale</SelectItem><SelectItem value="Altro">Altro</SelectItem></SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            <div className="border-t border-border pt-3">
              <Label className="text-xs uppercase tracking-wider text-slate-500">Consenso marketing (facoltativo)</Label>
              <p className="mb-2 text-xs text-slate-400">Nessuna opzione preselezionata. Ogni modifica viene storicizzata.</p>
              <div className="grid grid-cols-3 gap-2">
                <div className="space-y-1"><Label className="text-xs">Consenso</Label>
                  <Select value={f.marketing_consent || "Non espresso"} onValueChange={(v) => set("marketing_consent", v)}>
                    <SelectTrigger data-testid="ec-marketing-consent"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white"><SelectItem value="Non espresso">Non espresso</SelectItem><SelectItem value="Si">Sì</SelectItem><SelectItem value="No">No</SelectItem></SelectContent>
                  </Select>
                </div>
                <div className="space-y-1"><Label className="text-xs">Data</Label><Input type="date" value={f.marketing_consent_date || ""} onChange={(e) => set("marketing_consent_date", e.target.value)} data-testid="ec-marketing-date" /></div>
                <div className="space-y-1"><Label className="text-xs">Fonte</Label>
                  <Select value={f.marketing_consent_source || "none"} onValueChange={(v) => set("marketing_consent_source", v === "none" ? "" : v)}>
                    <SelectTrigger data-testid="ec-marketing-source"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent className="bg-white"><SelectItem value="none">—</SelectItem><SelectItem value="Sportello">Sportello</SelectItem><SelectItem value="Modulo cartaceo">Modulo cartaceo</SelectItem><SelectItem value="Web">Web</SelectItem><SelectItem value="Email">Email</SelectItem><SelectItem value="Altro">Altro</SelectItem></SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-1.5"><Label>Note generali</Label><Textarea value={f.notes} onChange={(e) => set("notes", e.target.value)} rows={3} /></div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="ec-cancel">Annulla</Button>
          <Button onClick={save} disabled={saving} data-testid="ec-save">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salva modifiche"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
